// packages/ui-patterns/src/MicButton.tsx
//
// Issue #1214 — this button hardcoded a single AAB-namespaced class
// (`aab-mic-button`). AtomicAIBuilder defines that class in its own
// stylesheet; RoleForge never did, so in RoleForge the control rendered as a
// bare unstyled icon: no button affordance, no recording state, and a hit
// target well under the 44px minimum.
//
// The fix is additive rather than a rename, so the AAB consumer keeps every
// selector it already styles:
//   • `aab-mic-button` / `is-<state>` / `is-<size>` are still emitted,
//   • a neutral `vc-mic-button` set is emitted alongside them for consumers
//     that want to style it without adopting AAB's namespace (RoleForge
//     defines these in apps/roleforge-app/src/index.css),
//   • `data-state` / `data-size` give both apps (and tests) a stable hook,
//   • the recording state is announced to screen readers, not just drawn.

import { useEffect, useRef, useState } from 'react';
import { Mic, Square } from 'lucide-react';

/** Screen-reader-only, framework-neutral (no Tailwind/global CSS needed). */
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

const STATE_ANNOUNCEMENT: Record<MicState, string> = {
  idle: '',
  recording: 'Recording. Choose stop when you have finished speaking.',
  transcribing: 'Turning what you said into text…',
  failed: 'Voice input did not work.',
};

/**
 * #1320. The server caps an upload at 25MB and multer's rejection carries no
 * status, so a long recording used to come back as an unparseable HTML 500 —
 * after `chunksRef` had already been cleared. Stopping on our own well before
 * the cap means the recording is SENT rather than lost, and the elapsed counter
 * means the limit is visible while it still matters.
 *
 * Four minutes at typical webm/opus dictation bitrates is comfortably inside
 * 25MB while being far longer than any real dictation turn.
 */
export const MIC_MAX_RECORDING_MS = 4 * 60 * 1000;

/** How long before the auto-stop the counter starts warning. */
const MIC_WARN_REMAINING_MS = 30 * 1000;

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

interface MicButtonProps {
  label: string;
  onTranscript: (text: string) => void;
  transcribe: (audio: Blob) => Promise<string>;
  onError?: (message: string) => void;
  /** #1320: surfaced when a recording is DISCARDED rather than failed. */
  onNotice?: (message: string) => void;
  disabled?: boolean;
  disabledTitle?: string;
  size?: 'sm' | 'md';
  className?: string;
  /** Test seam: override the auto-stop ceiling. */
  maxRecordingMs?: number;
}

type MicState = 'idle' | 'recording' | 'transcribing' | 'failed';

function describeError(err: unknown): string {
  if (err instanceof DOMException) {
    if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      return 'Microphone access was blocked. Allow it in your browser to dictate.';
    }
    if (err.name === 'NotFoundError' || err.name === 'OverconstrainedError') {
      return 'No microphone was found on this device.';
    }
    if (err.name === 'NotReadableError') {
      return 'The microphone is busy or unavailable. Try closing other apps.';
    }
    return err.message || `Microphone error: ${err.name}`;
  }
  if (err instanceof Error) return err.message;
  return 'Voice transcription failed.';
}

function hasMicSupport() {
  return Boolean(
    typeof navigator !== 'undefined' &&
      typeof navigator.mediaDevices?.getUserMedia === 'function' &&
      typeof MediaRecorder !== 'undefined',
  );
}

export function MicButton({
  label,
  onTranscript,
  transcribe,
  onError,
  onNotice,
  disabled = false,
  disabledTitle,
  size = 'md',
  className,
  maxRecordingMs = MIC_MAX_RECORDING_MS,
}: MicButtonProps) {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const discardOnStopRef = useRef(false);
  const unmountedRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);
  const [state, setState] = useState<MicState>('idle');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const supported = hasMicSupport();
  const recording = state === 'recording';
  const onNoticeRef = useRef(onNotice);
  useEffect(() => { onNoticeRef.current = onNotice; });

  function reportError(err: unknown) {
    const message = describeError(err);
    if (!unmountedRef.current) setErrorMessage(message);
    onError?.(message);
  }

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      cancelRecording();
    };
  }, []);

  // #1320: `if (disabled) cancelRecording()` threw an in-progress recording away
  // with NO feedback on that path. `CoachChatPanel` passes
  // `disabled={chatLocked || sending}`, so dictating, pressing Enter, and
  // dictating again made the second recording vanish in silence. The discard is
  // still correct — the mic genuinely became unavailable — but it is now
  // announced, so the learner knows to say it again rather than assuming it was
  // captured.
  useEffect(() => {
    if (!disabled) return;
    const wasRecording = recorderRef.current !== null && recorderRef.current.state !== 'inactive';
    cancelRecording();
    if (wasRecording) {
      onNoticeRef.current?.(
        "That recording stopped before it could be sent, so it wasn't saved. Say it again when the mic is free.",
      );
    }
  }, [disabled]);

  // #1320: a visible elapsed counter plus an auto-stop, so a long take is SENT
  // at the ceiling instead of silently exceeding the server's upload cap and
  // being destroyed by the failure path.
  useEffect(() => {
    if (!recording) {
      startedAtRef.current = null;
      setElapsedMs(0);
      return;
    }
    startedAtRef.current = Date.now();
    const tick = window.setInterval(() => {
      const startedAt = startedAtRef.current;
      if (startedAt === null) return;
      const next = Date.now() - startedAt;
      setElapsedMs(next);
      if (next >= maxRecordingMs) {
        onNoticeRef.current?.(
          'That was the longest recording we can send in one go, so we stopped and sent it. Carry on in a new recording.',
        );
        stop();
      }
    }, 250);
    return () => window.clearInterval(tick);
  }, [recording, maxRecordingMs]);

  function stopStream() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  function cancelRecording() {
    discardOnStopRef.current = true;
    try {
      const r = recorderRef.current;
      if (r && r.state !== 'inactive') r.stop();
    } catch { /* stream cleanup is the privacy boundary */ }
    finally {
      stopStream();
      recorderRef.current = null;
      chunksRef.current = [];
      if (!unmountedRef.current) setState('idle');
    }
  }

  async function start() {
    if (!supported || disabled) return;
    setErrorMessage(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      discardOnStopRef.current = false;

      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        if (discardOnStopRef.current || unmountedRef.current) {
          stopStream(); recorderRef.current = null; chunksRef.current = []; return;
        }
        setState('transcribing');
        try {
          const text = await transcribe(new Blob(chunksRef.current, { type: 'audio/webm' }));
          if (text.trim()) onTranscript(text);
          if (!unmountedRef.current) setState('idle');
        } catch (err) {
          reportError(err);
          if (!unmountedRef.current) setState('failed');
        } finally {
          stopStream(); recorderRef.current = null; chunksRef.current = [];
        }
      };

      recorderRef.current = recorder;
      recorder.start();
      if (!unmountedRef.current) setState('recording');
    } catch (err) {
      reportError(err);
      stopStream();
      recorderRef.current = null;
      if (!unmountedRef.current) setState('failed');
    }
  }

  function stop() { recorderRef.current?.stop(); }

  const tooltip = !supported
    ? 'Dictation is not available in this browser.'
    : state === 'failed' && errorMessage
      ? errorMessage
      : disabledTitle;

  const announcement = STATE_ANNOUNCEMENT[state];
  const remainingMs = maxRecordingMs - elapsedMs;
  const nearLimit = recording && remainingMs <= MIC_WARN_REMAINING_MS;

  return (
    <button
      type="button"
      className={[
        'aab-mic-button',
        `is-${state}`,
        `is-${size}`,
        'vc-mic-button',
        `vc-mic-button--${state}`,
        `vc-mic-button--${size}`,
        nearLimit ? 'vc-mic-button--near-limit' : null,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-state={state}
      data-size={size}
      data-elapsed={recording ? formatElapsed(elapsedMs) : undefined}
      data-near-limit={nearLimit ? 'true' : undefined}
      aria-label={recording ? `Stop ${label}` : `Start ${label}`}
      aria-pressed={recording}
      disabled={!supported || disabled || state === 'transcribing'}
      title={tooltip}
      onClick={() => (recording ? stop() : void start())}
    >
      {recording ? <Square size={16} /> : <Mic size={16} />}
      {/* #1320: the elapsed time is VISIBLE while recording, so the ceiling the
          auto-stop enforces is not a surprise. */}
      {recording && (
        <span className="vc-mic-button-elapsed" aria-hidden="true">
          {formatElapsed(elapsedMs)}
        </span>
      )}
      <span style={VISUALLY_HIDDEN} role="status" aria-live="polite">
        {announcement}
      </span>
    </button>
  );
}
